import { ReactElement, useCallback, useEffect, useRef } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Address } from 'viem';

import { useConnection } from 'modules/auth/hooks/useConnection';
import { Table } from 'modules/common/components/Table';
import { TableCellProps } from 'modules/common/components/Table/TableCell';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { useClaim } from 'modules/pool/hooks/useClaim';
import { useIsClaimable } from 'modules/pool/hooks/useIsClaimable';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IActionsCellProps extends TableCellProps {
  poolAddress: Address;
  isLSClaim?: boolean;
  executedAt: Date;
}

export function UnlockClaimCell({
  poolAddress,
  executedAt,
  isLSClaim,
  ...props
}: IActionsCellProps): ReactElement {
  const { t, keys } = useTranslation(translation);
  const { classes, cx } = useStyles();
  const isMountedRef = useRef(true);
  const { isSafeAccount } = useConnection();

  const { claim, resetClaim, isLoading } = useClaim(poolAddress, isLSClaim);
  const { onOpen, isOpened } = useDialog(KnownDialogs.claimSuccess);
  const { isClaimable, timeLeft } = useIsClaimable(executedAt, poolAddress);

  const handleClaim = useCallback(() => {
    if (!isMountedRef.current) {
      return;
    }

    void claim().then(data => {
      if (data && !isOpened && !isSafeAccount) {
        onOpen();
      }

      resetClaim();
    });
  }, [claim, isOpened, onOpen, resetClaim, isSafeAccount]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Table.Cell {...props}>
      {isClaimable ? (
        <Button
          fullWidth
          className={classes.button}
          color="primary"
          disabled={isLoading}
          size="medium"
          translate="no"
          variant="outlined"
          onClick={handleClaim}
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={24} />
          ) : (
            t(keys.claim)
          )}
        </Button>
      ) : (
        <Button
          disabled
          className={cx(classes.button, classes.timeLeftButton)}
          color="secondary"
          size="medium"
          translate="no"
        >
          {timeLeft}
        </Button>
      )}
    </Table.Cell>
  );
}
